import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Cpu, TrendingUp, TrendingDown, Shield, Globe, BarChart3,
  ArrowRight, Loader2, Zap, Server, DollarSign, Activity
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, Cell, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

const TABS = [
  { id: 'markets', icon: BarChart3, labelKey: 'compute.tabMarkets' },
  { id: 'analyze', icon: TrendingUp, labelKey: 'compute.tabAnalysis' },
  { id: 'hedge', icon: Shield, labelKey: 'compute.tabHedging' },
  { id: 'datacenters', icon: Globe, labelKey: 'compute.tabDataCenters' },
];

const chartTooltipStyle = {
  background: '#11141D', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 0, fontFamily: 'monospace', fontSize: 11
};

function MetricBox({ label, value, color = 'emerald' }) {
  const colors = {
    emerald: 'text-emerald-400 border-emerald-500/20',
    blue: 'text-blue-400 border-blue-500/20',
    amber: 'text-amber-400 border-amber-500/20',
    cyan: 'text-cyan-400 border-cyan-500/20',
    red: 'text-red-400 border-red-500/20',
    purple: 'text-purple-400 border-purple-500/20',
  };
  const c = colors[color] || colors.emerald;
  return (
    <div className={`bg-[#090B0F] border ${c.split(' ')[1]} p-3`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</p>
      <p className={`text-lg font-black font-mono ${c.split(' ')[0]}`}>{value}</p>
    </div>
  );
}

/* ─── MARKETS ─── */
function MarketsTab({ t }) {
  const { formatCurrency } = useCurrency();
  const [markets, setMarkets] = useState([]);
  const [prices, setPrices] = useState({});
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        api.get('/api/compute-commodities/markets'),
        api.get('/api/compute-commodities/prices'),
      ]);
      setMarkets(m.data.markets || []);
      setPrices(p.data.prices || {});
      if (!selectedAsset && m.data.markets?.length) setSelectedAsset(m.data.markets[0].id);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedAsset]);

  useEffect(() => { load(); }, [load]);

  const priceHistory = selectedAsset && prices[selectedAsset]?.history || [];

  if (loading && !markets.length) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Market table */}
      <div className="bg-[#090B0F] border border-white/[0.06] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="compute-markets-table">
            <thead>
              <tr className="text-slate-500 border-b border-white/[0.06] bg-[#0B0E13]">
                <th className="text-left py-3 px-3">{t('compute.asset')}</th>
                <th className="text-left py-3 px-2">{t('compute.type')}</th>
                <th className="text-right py-3 px-2">{t('compute.spotPrice')}</th>
                <th className="text-right py-3 px-2">24h</th>
                <th className="text-right py-3 px-2">7d</th>
                <th className="text-right py-3 px-2">{t('compute.availability')}</th>
                <th className="text-right py-3 px-2">{t('compute.demand')}</th>
                <th className="text-right py-3 px-3">{t('compute.volume')}</th>
              </tr>
            </thead>
            <tbody>
              {markets.map(m => (
                <tr key={m.id}
                  onClick={() => setSelectedAsset(m.id)}
                  className={`border-b border-white/[0.03] cursor-pointer transition-colors ${selectedAsset === m.id ? 'bg-emerald-500/5' : 'hover:bg-white/[0.02]'}`}
                  data-testid={`market-row-${m.id}`}>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-white font-bold">{m.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`text-[10px] px-1.5 py-0.5 font-bold ${m.type === 'GPU' ? 'bg-purple-500/10 text-purple-400' : m.type === 'TPU' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {m.type}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right text-white font-mono font-bold">${m.unit_price}</td>
                  <td className={`py-3 px-2 text-right font-mono ${m.change_24h_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {m.change_24h_pct >= 0 ? '+' : ''}{m.change_24h_pct}%
                  </td>
                  <td className={`py-3 px-2 text-right font-mono ${m.change_7d_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {m.change_7d_pct >= 0 ? '+' : ''}{m.change_7d_pct}%
                  </td>
                  <td className="py-3 px-2 text-right text-slate-300 font-mono">{m.availability_pct}%</td>
                  <td className="py-3 px-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <div className="w-12 h-1.5 bg-[#11141D] overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${m.demand_score * 100}%` }} />
                      </div>
                      <span className="text-slate-400 text-[10px] font-mono">{(m.demand_score * 100).toFixed(0)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-right text-slate-300 font-mono">${m.volume_24h_m}M</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Price chart for selected */}
      {selectedAsset && prices[selectedAsset] && (
        <div className="bg-[#090B0F] border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white">{prices[selectedAsset].name} — {t('compute.priceHistory90d')}</h3>
            <div className="flex items-center gap-4 text-[10px] font-mono">
              <span className="text-slate-500">H: <span className="text-emerald-400">${prices[selectedAsset].high_90d}</span></span>
              <span className="text-slate-500">L: <span className="text-red-400">${prices[selectedAsset].low_90d}</span></span>
              <span className="text-slate-500">Avg: <span className="text-white">${prices[selectedAsset].avg_90d}</span></span>
            </div>
          </div>
          <div className="h-[260px]" data-testid="compute-price-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={priceHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} domain={['auto', 'auto']} axisLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={chartTooltipStyle} formatter={v => [`$${v}`, 'Price']} />
                <Line type="monotone" dataKey="price" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── INVESTMENT ANALYSIS ─── */
function AnalysisTab({ t }) {
  const [form, setForm] = useState({ compute_type: 'H100', investment_amount: 100000, holding_period: 12, risk_tolerance: 'moderate' });
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/api/compute-commodities/analyze', form);
      setResult(res.data);
    } catch (e) { console.error(e); }
    setAnalyzing(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#090B0F] border border-white/[0.06] p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" /> {t('compute.investmentAnalysis')}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.computeType')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.compute_type} onChange={e => setForm(f => ({ ...f, compute_type: e.target.value }))} data-testid="analyze-type-select">
              {['H100', 'A100', 'H200', 'TPU_V5', 'TPU_V4', 'MI300X', 'STORAGE'].map(ct => <option key={ct} value={ct}>{ct}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.investment')}</label>
            <input type="number" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.investment_amount} onChange={e => setForm(f => ({ ...f, investment_amount: +e.target.value }))} data-testid="analyze-amount-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.holdingPeriod')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.holding_period} onChange={e => setForm(f => ({ ...f, holding_period: +e.target.value }))} data-testid="analyze-period-select">
              {[3, 6, 12, 24, 36].map(m => <option key={m} value={m}>{m} months</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.riskTolerance')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.risk_tolerance} onChange={e => setForm(f => ({ ...f, risk_tolerance: e.target.value }))} data-testid="analyze-risk-select">
              <option value="conservative">{t('compute.conservative')}</option>
              <option value="moderate">{t('compute.moderate')}</option>
              <option value="aggressive">{t('compute.aggressive')}</option>
            </select>
          </div>
        </div>
        <button onClick={analyze} disabled={analyzing}
          className="mt-4 px-6 py-2.5 bg-emerald-500 text-[#030508] font-bold text-xs tracking-wider hover:bg-emerald-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="run-analysis-btn">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {analyzing ? t('compute.analyzing') : t('compute.runAnalysis')}
        </button>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricBox label={t('compute.expectedReturn')} value={`${result.expected_return_pct}%`} color="emerald" />
            <MetricBox label={t('compute.appreciation')} value={`${result.expected_appreciation_pct}%`} color="blue" />
            <MetricBox label={t('compute.yield')} value={`${result.expected_yield_pct}%`} color="cyan" />
            <MetricBox label={t('compute.volatility')} value={`${result.volatility_pct}%`} color="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* AI Thesis */}
            <div className="bg-[#090B0F] border border-white/[0.06] p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white">{t('compute.aiThesis')}</h3>
                <span className={`text-xs px-2 py-1 font-bold ${result.recommendation === 'BUY' ? 'bg-emerald-500/10 text-emerald-400' : result.recommendation === 'HOLD' ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                  {result.recommendation}
                </span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">{result.thesis}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between text-slate-500"><span>{t('compute.confidence')}</span><span className="text-emerald-400 font-mono">{(result.confidence * 100).toFixed(0)}%</span></div>
                <div className="flex justify-between text-slate-500"><span>VaR 95%</span><span className="text-red-400 font-mono">${result.value_at_risk_95?.toLocaleString()}</span></div>
                <div className="flex justify-between text-slate-500"><span>{t('compute.demandGrowth')}</span><span className="text-emerald-400 font-mono">{result.demand_growth_pct}%</span></div>
                <div className="flex justify-between text-slate-500"><span>{t('compute.supplyGrowth')}</span><span className="text-blue-400 font-mono">{result.supply_growth_pct}%</span></div>
              </div>
            </div>

            {/* Return breakdown */}
            <div className="bg-[#090B0F] border border-white/[0.06] p-5">
              <h3 className="text-sm font-bold text-white mb-3">{t('compute.returnBreakdown')}</h3>
              <div className="h-[200px]" data-testid="analysis-return-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: t('compute.appreciation'), value: result.expected_appreciation_pct },
                    { name: t('compute.yield'), value: result.expected_yield_pct },
                    { name: t('compute.totalReturn'), value: result.expected_return_pct },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={v => [`${v}%`, 'Value']} />
                    <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                      <Cell fill="#3b82f6" />
                      <Cell fill="#06b6d4" />
                      <Cell fill="#10b981" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── HEDGING ─── */
function HedgingTab({ t }) {
  const [form, setForm] = useState({ monthly_compute_spend: 50000, hedge_duration: 6, coverage_percent: 80 });
  const [result, setResult] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const getQuote = async () => {
    setQuoting(true);
    try {
      const res = await api.post('/api/compute-commodities/hedge-quote', form);
      setResult(res.data);
    } catch (e) { console.error(e); }
    setQuoting(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#090B0F] border border-white/[0.06] p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" /> {t('compute.hedgeCalculator')}
        </h3>
        <div className="grid grid-cols-3 gap-3 max-w-lg">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.monthlySpend')}</label>
            <input type="number" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.monthly_compute_spend} onChange={e => setForm(f => ({ ...f, monthly_compute_spend: +e.target.value }))} data-testid="hedge-spend-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.duration')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.hedge_duration} onChange={e => setForm(f => ({ ...f, hedge_duration: +e.target.value }))} data-testid="hedge-duration-select">
              {[3, 6, 12, 18, 24].map(m => <option key={m} value={m}>{m} months</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('compute.coverage')}</label>
            <input type="number" min={10} max={100} className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.coverage_percent} onChange={e => setForm(f => ({ ...f, coverage_percent: +e.target.value }))} data-testid="hedge-coverage-input" />
          </div>
        </div>
        <button onClick={getQuote} disabled={quoting}
          className="mt-4 px-6 py-2.5 bg-amber-500 text-[#030508] font-bold text-xs tracking-wider hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="get-hedge-quote-btn">
          {quoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
          {quoting ? t('compute.quoting') : t('compute.getQuote')}
        </button>
      </div>

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Quote summary */}
          <div className="bg-[#090B0F] border border-amber-500/20 p-5">
            <h3 className="text-sm font-bold text-white mb-4">{t('compute.hedgeQuote')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-slate-400"><span>{t('compute.notional')}</span><span className="text-white font-mono font-bold">${result.notional?.toLocaleString()}</span></div>
              <div className="flex justify-between text-slate-400"><span>{t('compute.premium')}</span><span className="text-amber-400 font-mono font-bold">${result.premium?.toLocaleString()}</span></div>
              <div className="flex justify-between text-slate-400"><span>{t('compute.premiumPct')}</span><span className="text-amber-400 font-mono">{result.premium_pct}%</span></div>
              <div className="flex justify-between text-slate-400"><span>{t('compute.strikePrice')}</span><span className="text-white font-mono">{result.strike_price}x</span></div>
              <div className="flex justify-between text-slate-400"><span>{t('compute.breakeven')}</span><span className="text-cyan-400 font-mono">{result.breakeven_pct}%</span></div>
              <div className="border-t border-white/[0.06] pt-3 mt-3">
                <div className="flex justify-between text-slate-400"><span>{t('compute.savingsIf20')}</span><span className="text-emerald-400 font-mono font-bold">${result.savings_if_price_up_20?.toLocaleString()}</span></div>
                <div className="flex justify-between text-slate-400 mt-1"><span>{t('compute.costIfFlat')}</span><span className="text-red-400 font-mono">${result.cost_if_price_flat?.toLocaleString()}</span></div>
              </div>
            </div>
          </div>

          {/* Structure details */}
          <div className="bg-[#090B0F] border border-white/[0.06] p-5">
            <h3 className="text-sm font-bold text-white mb-4">{t('compute.hedgeStructure')}</h3>
            <div className="space-y-4">
              {Object.entries(result.hedge_structure || {}).map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{k.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-white font-medium">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── DATA CENTERS ─── */
function DataCentersTab({ t }) {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/compute-commodities/data-centers')
      .then(r => setFacilities(r.data.facilities || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const capData = facilities.map(f => ({ name: f.name.split(' ').slice(0, 2).join(' '), mw: f.capacity_mw, occupancy: f.occupancy_pct }));

  return (
    <div className="space-y-4">
      {/* Summary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricBox label={t('compute.totalFacilities')} value={facilities.length} color="emerald" />
        <MetricBox label={t('compute.totalCapacity')} value={`${facilities.reduce((s, f) => s + f.capacity_mw, 0)} MW`} color="blue" />
        <MetricBox label={t('compute.totalGpus')} value={facilities.reduce((s, f) => s + f.gpu_count, 0).toLocaleString()} color="purple" />
        <MetricBox label={t('compute.avgOccupancy')} value={`${(facilities.reduce((s, f) => s + f.occupancy_pct, 0) / Math.max(facilities.length, 1)).toFixed(0)}%`} color="cyan" />
      </div>

      {/* Capacity chart */}
      <div className="bg-[#090B0F] border border-white/[0.06] p-5">
        <h3 className="text-sm font-bold text-white mb-3">{t('compute.capacityByFacility')}</h3>
        <div className="h-[220px]" data-testid="datacenter-capacity-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={capData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Bar dataKey="mw" fill="#10b981" name="Capacity (MW)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="occupancy" fill="#3b82f6" name="Occupancy %" radius={[2, 2, 0, 0]} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Facility cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {facilities.map(f => (
          <div key={f.id} className="bg-[#090B0F] border border-white/[0.06] p-4 hover:border-emerald-500/20 transition-colors" data-testid={`facility-card-${f.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="text-sm font-bold text-white">{f.name}</h4>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{f.location}</p>
              </div>
              <span className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 font-bold">{f.provider}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div className="flex justify-between text-slate-500"><span>{t('compute.capacity')}</span><span className="text-white font-mono">{f.capacity_mw} MW</span></div>
              <div className="flex justify-between text-slate-500"><span>{t('compute.occupancy')}</span><span className="text-emerald-400 font-mono">{f.occupancy_pct}%</span></div>
              <div className="flex justify-between text-slate-500"><span>{t('compute.gpuCount')}</span><span className="text-purple-400 font-mono">{f.gpu_count.toLocaleString()}</span></div>
              <div className="flex justify-between text-slate-500"><span>{t('compute.yield')}</span><span className="text-cyan-400 font-mono">{f.yield_pct}%</span></div>
              <div className="flex justify-between text-slate-500"><span>PUE</span><span className="text-white font-mono">{f.pue}</span></div>
              <div className="flex justify-between text-slate-500"><span>{t('compute.greenEnergy')}</span><span className="text-emerald-400 font-mono">{f.green_energy_pct}%</span></div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
              <span className="text-[10px] text-slate-500">{t('compute.estRevenue')}</span>
              <span className="text-sm text-emerald-400 font-mono font-bold">${f.estimated_revenue_m}M</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function ComputeCommodities() {
  const [tab, setTab] = useState('markets');
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#030508] text-white p-4 sm:p-6" data-testid="compute-commodities-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Cpu className="w-6 h-6 text-cyan-400" />
          <h1 className="text-xl sm:text-2xl font-black tracking-tight">{t('compute.title')}</h1>
        </div>
        <p className="text-sm text-slate-500 ml-9">{t('compute.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1 border-b border-white/[0.06]">
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-wider whitespace-nowrap transition-colors ${
              tab === tb.id ? 'text-cyan-400 border-b-2 border-cyan-500 bg-cyan-500/5' : 'text-slate-500 hover:text-white'
            }`}
            data-testid={`compute-tab-${tb.id}`}>
            <tb.icon className="w-4 h-4" />
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'markets' && <MarketsTab t={t} />}
      {tab === 'analyze' && <AnalysisTab t={t} />}
      {tab === 'hedge' && <HedgingTab t={t} />}
      {tab === 'datacenters' && <DataCentersTab t={t} />}
    </div>
  );
}
